import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ref } from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useIsExpiredResetPassword,
  useResetPassword,
} from "../../api/use-forgot-password";
import { OkDialog } from "../ui/ok-dialog";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation(["auth", "validation", "common"]);
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  useEffect(() => {
    if (!hash) navigate("/");
  }, [hash, navigate]);

  const [{ data: isExpired, loading: isExpiredLoading }, executeIsExpired] =
    useIsExpiredResetPassword();

  useEffect(() => {
    const ex = async () => {
      await executeIsExpired({ data: { hash } });
    };
    ex().catch(console.log);
  }, [hash, executeIsExpired]);

  const schema = yup.object({
    password: yup
      .string()
      .min(8, t("minLengthValidation", { field: t("password"), min: 8 }))
      .max(120, t("maxLengthValidation", { field: t("password"), max: 120 }))
      .required(t("requiredValidation", { field: t("password") })),
    passwordAgain: yup
      .string()
      .oneOf([ref("password")], t("passwordDontMatch"))
      .required(t("requiredValidation", { field: t("passwordAgain") })),
  });
  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm({
    defaultValues: {
      password: "",
      passwordAgain: "",
    },
    resolver: yupResolver(schema),
  });
  const [{ loading }, execute] = useResetPassword();

  const onSubmit = async (data) => {
    try {
      await execute({
        data: {
          hash,
          password: data.password,
        },
      });
      setSent(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error_message ||
        (error.response?.status === 404
          ? t("resetPasswordErrorNotFound")
          : t("resetPasswordError"));
      setError("apiError", { message: errorMessage });
    }
  };

  const [sent, setSent] = useState(false);

  const handleOnClose = () => {
    navigate("/");
  };

  const handleOnCloseExpired = () => {
    navigate("/forgot-password");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
          overflow: "auto",
        }}
      >
        <Card
          sx={{
            px: 5,
            py: 3,
            width: "400px",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            margin: "auto",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              textTransform: "uppercase",
              mt: "15px",
            }}
          >
            {t("resetPassword")}
          </Typography>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              marginTop: "20px",
              "&>.MuiFormControl-root.MuiTextField-root": { mb: "16px" },
            }}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Controller
              render={({ field }) => (
                <TextField
                  fullWidth
                  size="small"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  label={t("newPassword")}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  variant="filled"
                  inputProps={{ maxLength: 255 }}
                  {...field}
                />
              )}
              control={control}
              name="password"
            />
            <Controller
              render={({ field }) => (
                <TextField
                  fullWidth
                  size="small"
                  error={!!errors.passwordAgain}
                  helperText={errors.passwordAgain?.message}
                  label={t("newPasswordAgain")}
                  name="password"
                  type="password"
                  variant="filled"
                  inputProps={{ maxLength: 255 }}
                  {...field}
                />
              )}
              control={control}
              name="passwordAgain"
            />
            {errors.apiError && (
              <Box sx={{ mt: 1 }}>
                <FormHelperText error>{errors.apiError.message}</FormHelperText>
              </Box>
            )}
            <LoadingButton
              loading={loading || isExpiredLoading}
              variant="black"
              size="small"
              type="submit"
              sx={{
                textTransform: "uppercase",
                marginTop: "10px",
              }}
            >
              {t("changePassword")}
            </LoadingButton>
          </Box>
        </Card>
        <OkDialog
          open={sent}
          onClose={handleOnClose}
          text={t("resetPasswordSuccess")}
          buttonText={t("login")}
        />
        <OkDialog
          open={isExpired && isExpired.expired}
          onClose={handleOnCloseExpired}
          text={t("resetPasswordExpired")}
          buttonText={t("forgotPasswordQuestion")}
        />
      </Box>
    </>
  );
}
